import { computeBill } from "@bulbee/pricing";
import { useState } from "react";

import type { Priced } from "./PlanFinder";
import type { PublishedPlan } from "./types";

const CONFIDENCE_LABEL: Record<string, { text: string; title: string }> = {
  high: {
    text: "Verified",
    title:
      "Our price calculation reproduces the 500, 1000 and 2000 kWh averages disclosed on this plan's Electricity Facts Label.",
  },
  medium: {
    text: "Close",
    title:
      "Our calculation is within a third of a cent of the disclosed averages but does not match exactly. Check the EFL before enrolling.",
  },
  low: {
    text: "Estimated",
    title:
      "We could not reconcile this plan's rate components with its disclosed averages, so prices are fitted to the published figures. Check the EFL before enrolling.",
  },
  unparsed: {
    text: "Estimated",
    title:
      "We could not read this plan's Electricity Facts Label, so prices are fitted to the figures Power to Choose publishes. Check the EFL before enrolling.",
  },
};

export function PlanCard({ row, rank, usage }: { row: Priced; rank: number; usage: number }) {
  const [open, setOpen] = useState(false);
  const { plan, bill } = row;
  const badge = CONFIDENCE_LABEL[plan.confidence] ?? CONFIDENCE_LABEL.unparsed;

  return (
    <li className="plan">
      <div className="plan-main">
        <span className="rank">{rank}</span>
        <div className="plan-id">
          <div className="rep">{plan.rep}</div>
          <div className="product">{plan.product}</div>
          <div className="tags">
            <span className="tag">{plan.rate_type}</span>
            {plan.term_months ? <span className="tag">{plan.term_months} mo</span> : null}
            {plan.renewable_pct ? <span className="tag green">{plan.renewable_pct}% renewable</span> : null}
            {plan.prepaid ? <span className="tag">Prepaid</span> : null}
            {plan.time_of_use ? <span className="tag warn">Time of use</span> : null}
            {plan.new_customer_only ? <span className="tag">New customers</span> : null}
            <span className={`tag badge ${plan.confidence}`} title={badge.title}>
              {badge.text}
            </span>
          </div>
        </div>
        <div className="price">
          <div className="cents">{bill.avg_cents_per_kwh.toFixed(1)}&cent;</div>
          <div className="per">per kWh at {usage.toLocaleString()} kWh</div>
          <div className="monthly">${bill.total_usd.toFixed(2)}/mo</div>
        </div>
        <button className="details-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide" : "Details"}
        </button>
      </div>

      {open && <PlanDetail plan={plan} usage={usage} />}
    </li>
  );
}

function PlanDetail({ plan, usage }: { plan: PublishedPlan; usage: number }) {
  const bill = computeBill(plan.rate, plan.tdu_charges, usage);
  const energy = plan.rate.energy_charge;

  return (
    <div className="plan-detail">
      <div className="detail-grid">
        <section>
          <h4>Your bill at {usage.toLocaleString()} kWh</h4>
          <dl className="breakdown">
            <Line label="Base charge" value={bill.base_charge_usd} />
            <Line
              label={`Energy (${energy?.kind === "tiered" ? "tiered" : `${(energy?.cents_per_kwh ?? 0).toFixed(4)}\u00a2/kWh`})`}
              value={bill.energy_usd}
            />
            {!plan.rate.tdu_charges_bundled && (
              <>
                <Line label="Delivery, monthly" value={bill.tdu_fixed_usd} />
                <Line
                  label={`Delivery (${(plan.tdu_charges.cents_per_kwh ?? 0).toFixed(4)}\u00a2/kWh)`}
                  value={bill.tdu_variable_usd}
                />
              </>
            )}
            {bill.bill_credit_usd > 0 && <Line label="Bill credit" value={-bill.bill_credit_usd} />}
            {bill.minimum_usage_fee_usd > 0 && (
              <Line label="Minimum usage fee" value={bill.minimum_usage_fee_usd} />
            )}
            <Line label="Total" value={bill.total_usd} strong />
          </dl>
          <p className="fineprint">
            Excludes state and local taxes and any non-recurring fees.
            {hasCancelFee(plan.cancel_fee) ? ` Early termination: ${plan.cancel_fee}.` : ""}
          </p>
        </section>

        <section>
          <h4>Our math vs. the disclosures</h4>
          <table className="compare">
            <thead>
              <tr>
                <th></th>
                <th>500</th>
                <th>1,000</th>
                <th>2,000</th>
              </tr>
            </thead>
            <tbody>
              <PriceRow label="On the EFL" values={plan.published.efl} />
              <PriceRow label="Power to Choose" values={plan.published.ptc} />
              <PriceRow label="Our calculation" values={plan.published.computed} />
            </tbody>
          </table>
          {plan.credit_text && <p className="fineprint">{plan.credit_text}</p>}
          {plan.issues.length > 0 && (
            <ul className="issues">
              {plan.issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="links">
        <a href={plan.efl_url} target="_blank" rel="noreferrer noopener">
          Electricity Facts Label
        </a>
        {plan.terms_url && (
          <a href={plan.terms_url} target="_blank" rel="noreferrer noopener">
            Terms of service
          </a>
        )}
        {plan.enroll_url && (
          <a className="enroll" href={plan.enroll_url} target="_blank" rel="noreferrer noopener">
            Enroll with {plan.rep}
          </a>
        )}
      </div>
    </div>
  );
}

/** Power to Choose stores "no fee" as an empty string, "0", or "0.00". */
function hasCancelFee(raw: string): boolean {
  const text = raw.trim();
  if (!text) return false;
  const numeric = Number(text.replace(/[$,]/g, ""));
  return Number.isNaN(numeric) || numeric > 0;
}

function Line({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <>
      <dt className={strong ? "strong" : undefined}>{label}</dt>
      <dd className={strong ? "strong" : undefined}>
        {value < 0 ? `-$${Math.abs(value).toFixed(2)}` : `$${value.toFixed(2)}`}
      </dd>
    </>
  );
}

function PriceRow({ label, values }: { label: string; values: Record<string, number> }) {
  return (
    <tr>
      <th scope="row">{label}</th>
      {["500", "1000", "2000"].map((usage) => (
        <td key={usage}>{values[usage] === undefined ? "-" : `${values[usage].toFixed(1)}\u00a2`}</td>
      ))}
    </tr>
  );
}
